import type { AnyComponent } from 'preact';
import { LocationProvider, Router, Route, ErrorBoundary, lazy } from 'preact-iso';
import { localizedPaths, useTranslation } from './i18n';
import { PageContainer } from './primitives';
import manifest from '../../../../resources/i18n/manifest.json';
import { LandingPage } from './pages/Landing';
import { CatalogPage } from './pages/Catalog';
import { WorkDetailPage } from './pages/WorkDetail';
import { ArticlesPage } from './pages/Articles';
import { ArticleDetailPage } from './pages/ArticleDetail';
import { StudioPage } from './pages/Studio';
import { SignInPage } from './pages/SignIn';
import { ProfilePage } from './pages/Profile';
import { MeRedirectPage } from './pages/MeRedirect';
import { AccountProfilePage } from './pages/me/Profile';
import { AccountVisibilityPage } from './pages/me/Visibility';
import { AccountSignInPage } from './pages/me/SignIn';
import { AccountGwtagsPage } from './pages/me/Gwtags';
import { AccountClaimsPage } from './pages/me/Claims';
import { AccountCollectionsPage } from './pages/me/Collections';
import { AccountDataPage } from './pages/me/Data';
import { AccountConnectPage } from './pages/me/Connect';
import { AccountConnectDonePage } from './pages/me/ConnectDone';
import { Layout } from './components/Layout';
import { AlertBanner } from './components/notifications/AlertBanner';
import { PublicFeedToast } from './components/notifications/PublicFeedToast';
import { withBase } from './paths';

// Admin pages are lazy-loaded: the curator console is a large slice
// (27 pages) that public visitors never touch, so it ships as its own
// chunk(s) fetched on first /admin navigation rather than inflating the
// public bundle. preact-iso's lazy() throws a promise while the chunk
// loads, caught by the <ErrorBoundary> already wrapping <Router> below.
//
// lazyPage() casts away lazy()'s generic-inference quirk: TS infers T as
// `{ default: Component }` instead of `Component` for `.then(m => ({default: ...}))`
// callbacks, which `AnyComponent<any>` then rejects.
function lazyPage(load: () => Promise<{ default: AnyComponent<any> }>): AnyComponent<any> {
    return lazy(load) as unknown as AnyComponent<any>;
}

const AdminDashboardPage = lazyPage(() => import('./pages/admin/Dashboard').then((m) => ({ default: m.AdminDashboardPage })));
const AdminOperationsPage = lazyPage(() => import('./pages/admin/Operations').then((m) => ({ default: m.AdminOperationsPage })));
const AdminSourcesPage = lazyPage(() => import('./pages/admin/Sources').then((m) => ({ default: m.AdminSourcesPage })));
const AdminSourceCredentialsPage = lazyPage(() => import('./pages/admin/SourceCredentials').then((m) => ({ default: m.AdminSourceCredentialsPage })));
const AdminRecrawlPage = lazyPage(() => import('./pages/admin/Recrawl').then((m) => ({ default: m.AdminRecrawlPage })));
const AdminWorksPage = lazyPage(() => import('./pages/admin/Works').then((m) => ({ default: m.AdminWorksPage })));
const AdminDuplicatesPage = lazyPage(() => import('./pages/admin/Duplicates').then((m) => ({ default: m.AdminDuplicatesPage })));
const AdminImportPage = lazyPage(() => import('./pages/admin/Import').then((m) => ({ default: m.AdminImportPage })));
const AdminArticlesPage = lazyPage(() => import('./pages/admin/Articles').then((m) => ({ default: m.AdminArticlesPage })));
const AdminArticleEditPage = lazyPage(() => import('./pages/admin/ArticleEdit').then((m) => ({ default: m.AdminArticleEditPage })));
const AdminTranslationSettingsPage = lazyPage(() => import('./pages/admin/TranslationSettings').then((m) => ({ default: m.AdminTranslationSettingsPage })));
const AdminAiServersPage = lazyPage(() => import('./pages/admin/AiServers').then((m) => ({ default: m.AdminAiServersPage })));
const AdminGlossaryPage = lazyPage(() => import('./pages/admin/Glossary').then((m) => ({ default: m.AdminGlossaryPage })));
const AdminEnrichmentRulesPage = lazyPage(() => import('./pages/admin/EnrichmentRules').then((m) => ({ default: m.AdminEnrichmentRulesPage })));
const AdminCalibrationPage = lazyPage(() => import('./pages/admin/Calibration').then((m) => ({ default: m.AdminCalibrationPage })));
const AdminTasksPage = lazyPage(() => import('./pages/admin/Tasks').then((m) => ({ default: m.AdminTasksPage })));
const AdminAuditPage = lazyPage(() => import('./pages/admin/Audit').then((m) => ({ default: m.AdminAuditPage })));
const AdminAboutPage = lazyPage(() => import('./pages/admin/About').then((m) => ({ default: m.AdminAboutPage })));
const AdminCreatorsPage = lazyPage(() => import('./pages/admin/Creators').then((m) => ({ default: m.AdminCreatorsPage })));
const AdminCreatorDetailPage = lazyPage(() => import('./pages/admin/CreatorDetail').then((m) => ({ default: m.AdminCreatorDetailPage })));
const AdminPlatformsPage = lazyPage(() => import('./pages/admin/Platforms').then((m) => ({ default: m.AdminPlatformsPage })));
const AdminGamesPage = lazyPage(() => import('./pages/admin/Games').then((m) => ({ default: m.AdminGamesPage })));
const AdminOAuthBindingsPage = lazyPage(() => import('./pages/admin/OAuthBindings').then((m) => ({ default: m.AdminOAuthBindingsPage })));
const AdminAuthorClaimsPage = lazyPage(() => import('./pages/admin/AuthorClaims').then((m) => ({ default: m.AdminAuthorClaimsPage })));
const AdminDeclinedAccountsPage = lazyPage(() => import('./pages/admin/DeclinedAccounts').then((m) => ({ default: m.AdminDeclinedAccountsPage })));

// Routes are root-relative + locale-aware. EN (the default locale,
// urlPrefix="") mounts at the canonical paths; non-default locales
// mount each canonical path under their prefix.
// localizedPaths() expands a canonical path into one entry per locale,
// so adding a new locale to manifest.json regenerates routes without
// code edits.
//
// Admin routes register here when the admin slice (commit 3) lands.

type RouteEntry = { path: string; component: AnyComponent<any> };

export function App() {
    const pages: RouteEntry[] = [
        { path: '/', component: LandingPage },
        { path: '/emporium', component: CatalogPage },
        // Id-routed with a decorative trailing slug (E3): the id resolves, the
        // slug is cosmetic. The two-segment form is listed before the bare id
        // form so preact-iso (first match wins) prefers it when a slug is present.
        { path: '/work/:id/:slug', component: WorkDetailPage },
        { path: '/work/:id', component: WorkDetailPage },
        { path: '/articles', component: ArticlesPage },
        { path: '/articles/:id/:slug', component: ArticleDetailPage },
        { path: '/articles/:id', component: ArticleDetailPage },
        { path: '/studio', component: StudioPage },
        { path: '/signin', component: SignInPage },
        { path: '/me', component: MeRedirectPage },
        { path: '/me/account', component: AccountProfilePage },
        { path: '/me/account/profile', component: AccountProfilePage },
        { path: '/me/account/visibility', component: AccountVisibilityPage },
        { path: '/me/account/sign-in', component: AccountSignInPage },
        { path: '/me/account/gwtags', component: AccountGwtagsPage },
        { path: '/me/account/claims', component: AccountClaimsPage },
        { path: '/me/account/collections', component: AccountCollectionsPage },
        { path: '/me/account/data', component: AccountDataPage },
        { path: '/u/:slug', component: ProfilePage },
    ];

    // MCP OAuth consent on-ramp (SEC-0006 / prompt §8). Non-localized like admin - these are an
    // OAuth protocol bridge the framework's Authorization Server drives the browser to, not
    // content pages, and they are intentionally absent from the account nav. The /oauth/* seam
    // they consume ships with SEC-0006; the pages are built to its contract now.
    const connectRoutes: RouteEntry[] = [
        { path: '/me/connect', component: AccountConnectPage },
        { path: '/me/connect/done', component: AccountConnectDonePage },
    ];

    // Admin routes stay locale-bare on purpose - the admin surface is
    // English-only for the curator-facing first cut. Add to
    // `localizedPaths()` later if/when we decide to translate it.
    const adminRoutes: RouteEntry[] = [
        { path: '/admin', component: AdminDashboardPage },
        { path: '/admin/operations', component: AdminOperationsPage },
        { path: '/admin/sources', component: AdminSourcesPage },
        { path: '/admin/source-credentials', component: AdminSourceCredentialsPage },
        { path: '/admin/recrawl', component: AdminRecrawlPage },
        { path: '/admin/articles', component: AdminArticlesPage },
        { path: '/admin/articles/new', component: AdminArticleEditPage },
        { path: '/admin/articles/:id', component: AdminArticleEditPage },
        { path: '/admin/works', component: AdminWorksPage },
        { path: '/admin/duplicates', component: AdminDuplicatesPage },
        { path: '/admin/duplicates/:pairKey', component: AdminDuplicatesPage },
        { path: '/admin/enrichment-rules', component: AdminEnrichmentRulesPage },
        { path: '/admin/calibration', component: AdminCalibrationPage },
        { path: '/admin/tasks', component: AdminTasksPage },
        { path: '/admin/audit', component: AdminAuditPage },
        { path: '/admin/about', component: AdminAboutPage },
        { path: '/admin/creators', component: AdminCreatorsPage },
        { path: '/admin/creators/:id', component: AdminCreatorDetailPage },
        { path: '/admin/platforms', component: AdminPlatformsPage },
        { path: '/admin/games', component: AdminGamesPage },
        { path: '/admin/oauth-bindings', component: AdminOAuthBindingsPage },
        { path: '/admin/claims', component: AdminAuthorClaimsPage },
        { path: '/admin/declined-accounts', component: AdminDeclinedAccountsPage },
        { path: '/admin/translation/settings', component: AdminTranslationSettingsPage },
        { path: '/admin/translation/ai-servers', component: AdminAiServersPage },
        { path: '/admin/translation/glossary', component: AdminGlossaryPage },
        { path: '/admin/import', component: AdminImportPage },
    ];

    return (
        <LocationProvider>
            <ErrorBoundary>
                <AlertBanner />
                <PublicFeedToast />
                <Router>
                    {pages.flatMap((p) =>
                        localizedPaths(p.path, manifest).map((localizedPath) => (
                            <Route
                                key={localizedPath}
                                path={withBase(localizedPath)}
                                component={p.component}
                            />
                        )),
                    )}
                    {adminRoutes.map((r) => (
                        <Route
                            key={r.path}
                            path={withBase(r.path)}
                            component={r.component}
                        />
                    ))}
                    {connectRoutes.map((r) => (
                        <Route
                            key={r.path}
                            path={withBase(r.path)}
                            component={r.component}
                        />
                    ))}
                    <Route default component={NotFound} />
                </Router>
            </ErrorBoundary>
        </LocationProvider>
    );
}

function NotFound() {
    const t = useTranslation();
    return (
        <Layout>
            <PageContainer cap="readable">
                <div class="grid place-items-center py-16 text-center">
                    <div>
                        <h1 class="text-3xl font-bold">{t('notFound.title')}</h1>
                        <p class="mt-2 text-fg-secondary">
                            {t('notFound.body')}{' '}
                            <a href={withBase('/')} class="text-accent hover:text-accent-soft">
                                {t('notFound.cta')}
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </PageContainer>
        </Layout>
    );
}
